import { MaxTagSize } from '../../utils/commonConstants';
import TagUtils from '../../utils/tagUtils';
import RupTooltip from '../rupTooltip';
import { Theme } from '@emotion/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { SxProps } from '@mui/system';
import { Colors, Icons } from 'copyright-clearance-center-common-ui';
import { forwardRef } from 'react';

const BorderlessTextField = styled(TextField)({
	'& .MuiOutlinedInput-root': {
		'& fieldset': {
			border: 'none'
		},
		'&:hover fieldset': {
			border: 'none'
		},
		'&.Mui-focused fieldset': {
			border: 'none'
		},
		padding: 0
	},
	flexBasis: '100%'
});

export interface ITagsInputTag {
	exists?: boolean;
	tag: string;
}

export interface ITagsInputProps {
	onInput: (input: string) => void;
	onTagsAppend?: (tags: string[]) => boolean;
	searchOnly?: boolean;
	inputValue?: string;
	fontSize?: string;
	sx?: SxProps<Theme>;
}

const TagsInput: React.ForwardRefExoticComponent<ITagsInputProps & React.RefAttributes<HTMLInputElement>> = forwardRef(
	({ onTagsAppend, inputValue, onInput, searchOnly, fontSize, sx }, ref) => {
		const separateButtons = ['Tab', ';', ',', 'Enter'];

		const getPlaceholder = () => {
			if (searchOnly) {
				return localize('Search tags here');
			}

			return localize('Search or add tags here');
		};

		return (
			<Stack spacing={4} direction="row" data-test-id="test-input" sx={sx}>
				<Stack
					tabIndex={0}
					direction="row"
					border={`1px solid ${Colors.neutral500}`}
					borderRadius={1}
					flexGrow="1"
					paddingX={3}
					paddingY={2}
					flexWrap="wrap"
					gap={1}
					overflow="hidden"
				>
					<BorderlessTextField
						placeholder={getPlaceholder()}
						value={inputValue}
						inputProps={{ maxLength: MaxTagSize, 'data-test-id': 'tags-input' }}
						multiline
						onChange={e => {
							onInput(TagUtils.cleanup(e.target.value));
						}}
						onPaste={e => {
							e.preventDefault();
							const pastedData = e.clipboardData.getData('text/plain');

							if (searchOnly) {
								onInput(inputValue + TagUtils.cleanup(pastedData));
								return;
							}

							const newTags = TagUtils.cleanup(TagUtils.split(pastedData));

							if (newTags?.length > 1) {
								const uniqueTags = [
									...new Set(
										newTags.map(t => TagUtils.clipLength(t.trim())).filter(t => t.length > 0)
									)
								];
								if (uniqueTags.length > 0) {
									onTagsAppend?.(uniqueTags);
								}
							} else if (newTags?.length === 1) {
								onInput(TagUtils.clipLength(inputValue + newTags[0]));
							}
						}}
						onKeyDown={e => {
							if (!searchOnly && separateButtons.includes(e.key)) {
								e.preventDefault();

								const tag = inputValue.trim();
								if (tag.length > 0) {
									const appended = onTagsAppend?.([tag]);
									if (appended) {
										onInput('');
									}
								}
							}
						}}
						InputProps={{ style: { fontSize: fontSize } }}
						inputRef={ref}
					/>
				</Stack>
				<Box hidden={searchOnly} sx={{ fill: Colors.primary500 }} paddingTop={2} minWidth="1rem">
					<RupTooltip
						title={<div dangerouslySetInnerHTML={{ __html: TagUtils.tooltipText }} />}
						sx={{ maxWidth: '20rem' }}
						arrow
					>
						<span dangerouslySetInnerHTML={{ __html: Icons.InfoIcon }} />
					</RupTooltip>
				</Box>
			</Stack>
		);
	}
);
TagsInput.displayName = 'TagsInput';

export default TagsInput;
